import React, { useEffect } from "react";
import { useApplePaySdk } from "@insite/client-framework/Common/Hooks/useApplePaySdk";
import { validateApplePayMerchant } from "@insite/client-framework/Services/SettingsService";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadApplePayConfig from "@insite/client-framework/Store/Context/Handlers/LoadApplePayConfig";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import { connect, ResolveThunks } from "react-redux";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import {
    getCartState,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";

interface OwnProps {
    amount: number;
    countryCode: string;
    currencyCode: string;
    onSuccess?: (paymentData: any) => void;
    onError?: (error: any) => void;
    onValidate?: () => boolean;
    extendedStyles?: ApplePayButtonWrapperStyles;
    error?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId, isPlacingOrder, isCheckingOutWithPayPay } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const billToState = getBillToState(state, cartState.value?.billToId);
    const shipToState = getShipToState(state, cartState.value?.shipToId);

    const { sessionId, redirectResult } = parseQueryString<{ sessionId?: string; redirectResult?: string }>(
        getLocation(state).search,
    );

    return {
        isDisabled:
            cartState.isLoading ||
            billToState.isLoading ||
            !billToState.value ||
            shipToState.isLoading ||
            !shipToState.value ||
            !billToState.value.address1 ||
            !shipToState.value.address1 ||
            isPlacingOrder ||
            isCheckingOutWithPayPay ||
            (!!sessionId && !!redirectResult) ||
            hasProductsWithInvalidPrice(cartState.value) ||
            cartState.value?.hasInsufficientInventory === true ||
            !cartState.value?.canCheckOut,
        applePayConfig: state.context.applePayConfig,
    };
};

const mapDispatchToProps = {
    loadApplePayConfig,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ApplePayButtonWrapperStyles {
    container?: InjectableCss;
    button?: InjectableCss;
    errorText?: TypographyPresentationProps;
}

export const applePayButtonWrapperStyles: ApplePayButtonWrapperStyles = {
    container: {
        css: css`
            padding-top: 1em;
            width: 100%;
        `,
    },
    button: {
        css: css`
            position: relative;

            --apple-pay-button-width: 100%;
            --apple-pay-button-height: 40px;
            --apple-pay-button-border-radius: 4px;
            --apple-pay-button-padding: 0px 0px;
            --apple-pay-button-box-sizing: border-box;

            &:hover {
                opacity: 0.8;
            }
        `,
    },
    errorText: {
        color: "danger",
        weight: 600,
    },
};

const ApplePayButtonWrapper: React.FC<Props> = ({
    amount,
    countryCode,
    currencyCode,
    isDisabled,
    onSuccess,
    onError,
    onValidate,
    extendedStyles,
    error,
    applePayConfig,
    loadApplePayConfig,
}) => {
    const [styles] = React.useState(() => mergeToNew(applePayButtonWrapperStyles, extendedStyles));
    const isApplePayAvailable = useApplePaySdk(applePayConfig?.merchantId);
    useEffect(() => {
        if (!applePayConfig) {
            loadApplePayConfig();
        }
    }, [applePayConfig, loadApplePayConfig]);

    const handleClick = async () => {
        if (isDisabled) {
            return;
        }

        const isValid = onValidate?.();
        if (isValid === false) {
            return;
        }

        try {
            const paymentRequest = {
                countryCode,
                currencyCode,
                supportedNetworks: ["visa", "masterCard", "amex", "discover", "jcb"],
                merchantCapabilities: ["supports3DS"],
                total: {
                    label: applePayConfig!.merchantName,
                    amount: amount.toFixed(2),
                },
            };

            const session = new (ApplePaySession as any)(3, paymentRequest);

            session.onvalidatemerchant = async (event: any) => {
                try {
                    const merchantSession = await validateApplePayMerchant({
                        validationUrl: event.validationURL,
                        domainName: window.location.hostname,
                    });
                    session.completeMerchantValidation(merchantSession);
                } catch (validationError) {
                    session.abort();
                    onError?.(`Merchant validation error: ${validationError}`);
                }
            };

            session.onpaymentauthorized = (event: any) => {
                onSuccess?.(event.payment.token);
                session.completePayment(ApplePaySession.STATUS_SUCCESS);
            };

            session.oncancel = () => {};

            session.begin();
        } catch (error: any) {
            onError?.(error.message || "Apple Pay error");
        }
    };

    if (!isApplePayAvailable || !applePayConfig?.merchantId || !applePayConfig?.merchantName) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="applePayButton">
            <StyledWrapper {...styles.button}>
                {React.createElement("apple-pay-button", {
                    buttonstyle: "black",
                    type: "plain",
                    locale: "en-US",
                })}
                <div
                    role="button"
                    tabIndex={0}
                    onClick={handleClick}
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                            handleClick();
                        }
                    }}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.6 : 1,
                    }}
                />
            </StyledWrapper>
            {error && (
                <Typography {...styles.errorText} data-test-selector="applePayButtonError">
                    {error}
                </Typography>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ApplePayButtonWrapper);
